import { Color, MathUtils } from 'three';

// Sistema d'hores: donada una hora (0–24, decimal) retorna la configuració d'ambient (posició del
// SOL i de la LLUNA per separat, quantitat de llum, colors del cel/mar, temperatura càlida/freda).
//
// IDEES CLAU (feedback):
//  · SOL i LLUNA són cossos INDEPENDENTS. El sol dona la llum del dia; la lluna té la seva pròpia
//    intensitat i reflex. Quan el sol es pon, la lluna manté l'escena il·luminada → MAI pantalla negra.
//  · La "quantitat de llum" general segueix un horari propi (no lligat a que el sol sigui amunt):
//    matí suau (6–10), guanya (10–12), PIC 12–16 (màxim 14), posta 17–20, cau 20–22, fosc 23–4,
//    torna a il·luminar-se 4–6. La nit segueix sent visible (moonlit), no negra.
//  · Sol i lluna a COSTATS OPOSATS: tots dos creuen esq→dreta però en finestres decalades, així a la
//    posta (~20h) el sol és a la dreta-baix i la lluna surt per l'esquerra-baix (coexisteixen).
//  · TEMPERATURA: de dia colors més CÀLIDS, de nit més FREDS (ho aplica el compositor amb uTempMix).
//
// càmera2D: fov vertical ~33° → només es veu fins a ~16° sobre l'horitzó; els cossos van BAIXOS
// (elevació < ~15°) i azimut ~180 (davant) perquè es vegin ells i el seu reflex al mar.

// ── Cicle del SOL i de la LLUNA (tunejables) ────────────────────────────────────────
// azL = azimut a l'inici de l'arc (ESQUERRA de pantalla), azR = al final (DRETA). Mateix mapping
// per tots dos → en finestres decalades queden a costats oposats a la coexistència.
export const SUN_CYCLE = {
  rise: 6, set: 20, peakHour: 14, peakElev: 14, azL: 202, azR: 158,
};
export const MOON_CYCLE = {
  rise: 20, set: 6, peakHour: 25, peakElev: 12, azL: 202, azR: 158, // set<rise → creua mitjanit
};

// ── Llum (dia/nit): quantitat de llum + temperatura. Tot tunejable en viu ───────────
export const LIGHT_SETTINGS = {
  dayExposure: 0.13,    // exposició al pic del dia (baixa → escena FOSCA; el clar només al sol)
  nightFloor: 0.11,     // exposició base de nit (moonlit; prou perquè es vegi el mar, no negre)
  moonBoost: 0.05,      // llum extra que aporta la lluna (× moonlight)
};

// Origen de l'hora: si useDeviceTime = true, es fa servir l'hora real del dispositiu; si false,
// s'usa sempre fixedHour (per defecte les 12). Scene ho resol a l'iniciar l'escena.
export const TIME_SETTINGS = {
  useDeviceTime: false, // false = hora fixa (producció controlada); true = hora del navegador
  fixedHour: 12,        // hora que s'aplica quan useDeviceTime = false
};

// ── Ancoratges (només FORMA del cel + color del mar per hora; NO posició ni exposició) ──
const ANCHORS = [
  // hour rayl  turb  mie     gd     waterColor
  { h: 0,  ra: 0.25, tu: 2,   mi: 0.0025, gd: 0.95, wc: 0x010845 },  // mitjanit
  { h: 5,  ra: 0.5,  tu: 3,   mi: 0.0035, gd: 0.94, wc: 0x010845 },  // pre-albada
  { h: 7,  ra: 1.4,  tu: 6,   mi: 0.0050, gd: 0.95, wc: 0x010845 },  // sortida de sol
  { h: 10, ra: 0.9,  tu: 1.3, mi: 0.0022, gd: 0.97, wc: 0x010845 },  // matí (cel fosc, halo del sol)
  { h: 13, ra: 0.9,  tu: 1.1, mi: 0.0020, gd: 0.97, wc: 0x010845 },  // migdia (cel fosc, halo del sol)
  { h: 16, ra: 2.4,  tu: 1.3, mi: 0.0022, gd: 0.95, wc: 0x010845 },  // tarda
  { h: 19, ra: 1.4,  tu: 7,   mi: 0.0070, gd: 0.95, wc: 0x010845 },  // posta
  { h: 21, ra: 0.6,  tu: 2.5, mi: 0.0035, gd: 0.94, wc: 0x010845 },  // capvespre → nit
  { h: 24, ra: 0.30, tu: 2,   mi: 0.0030, gd: 0.94, wc: 0x010845 },  // = mitjanit (wrap)
];

// Corbes de llum (keyframes hora→valor 0..1), interpolades suau. Segueixen l'horari demanat.
const DAYLIGHT = [
  [0, 0], [4, 0], [5, 0.10], [6, 0.26], [10, 0.46], [11, 0.62], [12, 0.92],
  [14, 1.0], [16, 0.92], [17, 0.80], [18, 0.62], [19, 0.42], [20, 0.26],
  [21, 0.12], [22, 0.05], [23, 0], [24, 0],
];
const MOONLIGHT = [
  [0, 1.0], [3, 1.0], [4, 0.88], [5, 0.5], [6, 0.15], [7, 0], [19, 0],
  [20, 0.22], [21, 0.48], [22, 0.74], [23, 0.92], [24, 1.0],
];
// Temperatura del color (0 = fred, 1 = càlid). Natural: nit freda, ALBADA i POSTA càlides (vermells/
// taronges), migdia neutre-tebi. Dona els "matisos" de les diferents hores.
const WARMTH = [
  [0, 0.0], [4, 0.05], [5, 0.4], [6, 0.75], [7, 0.98], [8, 0.7], [9, 0.5],
  [11, 0.4], [13, 0.4], [15, 0.48], [16, 0.62], [17, 0.82], [18, 0.96],
  [19, 1.0], [20, 0.7], [21, 0.38], [22, 0.15], [23, 0.0], [24, 0.0],
];

const _cA = new Color();
const _cB = new Color();

function lerpHex(a, b, t) {
  _cA.setHex(a); _cB.setHex(b);
  return _cA.clone().lerp(_cB, t);
}

const smooth = (t) => t * t * (3 - 2 * t);

// Interpola una corba de keyframes [ [hora, valor], ... ] (hores creixents, cobrint 0..24).
function curve(h, points) {
  const H = ((h % 24) + 24) % 24;
  for (let i = 0; i < points.length - 1; i++) {
    const [h0, v0] = points[i], [h1, v1] = points[i + 1];
    if (H >= h0 && H <= h1) {
      const t = h1 === h0 ? 0 : (H - h0) / (h1 - h0);
      return v0 + (v1 - v0) * smooth(t);
    }
  }
  return points[points.length - 1][1];
}

// Arc d'un cos celeste: elevació 0 als extrems i pic (peakElev) a peakHour; azimut esq→dreta al
// llarg de l'arc. Fora de la finestra [rise,set] queda clarament SOTA l'horitzó (belowElev).
// Gestiona el creuament de mitjanit (set <= rise, p.ex. la lluna 20→6).
function arcAt(h, c, belowElev = -12) {
  let hh = ((h % 24) + 24) % 24;
  let set = c.set;
  let peak = c.peakHour;
  if (set <= c.rise) {                       // creua mitjanit
    set += 24;
    if (hh < c.rise) hh += 24;
    if (peak < c.rise) peak += 24;
  }
  const active = hh >= c.rise && hh <= set;
  let q;                                      // 0 a rise, 0.5 a peak, 1 a set
  if (hh <= peak) q = 0.5 * (hh - c.rise) / (peak - c.rise);
  else q = 0.5 + 0.5 * (hh - peak) / (set - peak);
  q = MathUtils.clamp(q, 0, 1);
  const elevation = active ? c.peakElev * Math.sin(Math.PI * q) : belowElev;
  const azimuth = c.azL + (c.azR - c.azL) * q; // esq → dreta
  return { elevation, azimuth, active };
}

export default class TimeOfDay {
  // Hora actual del navegador del client (0–24 decimal).
  static now() {
    const d = new Date();
    return d.getHours() + d.getMinutes() / 60 + d.getSeconds() / 3600;
  }

  // Posició del sol (per si es vol des de fora).
  static sunPosition(hour) { return arcAt(hour, SUN_CYCLE); }
  static moonPosition(hour) { return arcAt(hour, MOON_CYCLE); }

  // Configuració completa per a una hora donada.
  static config(hour) {
    const h = ((hour % 24) + 24) % 24;
    let a = ANCHORS[0], b = ANCHORS[ANCHORS.length - 1];
    for (let i = 0; i < ANCHORS.length - 1; i++) {
      if (h >= ANCHORS[i].h && h <= ANCHORS[i + 1].h) { a = ANCHORS[i]; b = ANCHORS[i + 1]; break; }
    }
    const t = a.h === b.h ? 0 : MathUtils.clamp((h - a.h) / (b.h - a.h), 0, 1);
    const L = (x, y) => MathUtils.lerp(x, y, t);

    const sun = arcAt(h, SUN_CYCLE);
    const moon = arcAt(h, MOON_CYCLE);
    const daylight = curve(h, DAYLIGHT);   // 0..1 (llum del dia)
    const moonlight = curve(h, MOONLIGHT); // 0..1 (intensitat de la lluna)
    const warmth = curve(h, WARMTH);       // 0..1 (temperatura: albada/posta càlides)

    // Quantitat de llum general: base moonlit + dia (segons daylight) + un extra per la lluna.
    // Mai baixa del floor → l'escena SEMPRE és visible, encara que el sol estigui post.
    const S = LIGHT_SETTINGS;
    const exposure = S.nightFloor + (S.dayExposure - S.nightFloor) * daylight + S.moonBoost * moonlight;

    // Estrelles: visibles quan hi ha poca llum de dia.
    const nightFactor = MathUtils.clamp(1 - daylight / 0.4, 0, 1);

    return {
      sun,                                 // {elevation, azimuth, active}
      moon,                                // {elevation, azimuth, active}
      moonIntensity: moonlight,            // 0..1 → opacitat/glow de la lluna + reflex al mar
      daylight,                            // 0..1 → intensitat del sol + tria del reflex
      warmth,                              // 0..1 → temperatura del color (albada/posta càlides)
      exposure,
      rayleigh: L(a.ra, b.ra),
      turbidity: L(a.tu, b.tu),
      mieCoefficient: L(a.mi, b.mi),
      mieDirectionalG: L(a.gd, b.gd),
      waterColor: lerpHex(a.wc, b.wc, t),
      nightFactor,                         // 1 = nit (estrelles visibles), 0 = dia
    };
  }
}
